import { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { newProjectStatuses } from '../../constants/statusOptions';
import { COUNTRIES, provincesForCountry } from '../../constants/geography';
import { createClient, getClients, updateClient } from '../../services/clientService';
import { createSite, getSites } from '../../services/siteService';
import { createProject, getNextProjectId, getProjects } from '../../services/projectService';
import { DuplicateWarning } from '../../components/forms/DuplicateWarning';
import type { Client, Project, Site } from '../../types/project';

interface NewProjectDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (project: Project) => void;
  initialClients: Client[];
  initialSites: Site[];
}

const ADD_NEW_CLIENT = '__add_new_client__';
const ADD_NEW_SITE = '__add_new_site__';
const todayStr = () => new Date().toISOString().split('T')[0];

export const NewProjectDialog = ({
  open,
  onClose,
  onCreated,
  initialClients,
  initialSites,
}: NewProjectDialogProps) => {
  const [clients, setClients] = useState<Client[]>(initialClients);
  const [sites, setSites] = useState<Site[]>(initialSites);
  const [allProjects, setAllProjects] = useState<Project[]>([]);

  const [projectId, setProjectId] = useState('');
  const [projectName, setProjectName] = useState('');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const [projectContact, setProjectContact] = useState('');
  const [projectType, setProjectType] = useState<'Internal' | 'External'>('External');
  const [clientContactName, setClientContactName] = useState('');
  const [clientContactEmail, setClientContactEmail] = useState('');
  const [clientContactPhone, setClientContactPhone] = useState('');
  const [status, setStatus] = useState('Active');
  const [expectedStartDate, setExpectedStartDate] = useState(todayStr());
  const [expectedEndDate, setExpectedEndDate] = useState('');
  const [comments, setComments] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [newClientOpen, setNewClientOpen] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newClientContact, setNewClientContact] = useState('');
  const [newClientEmail, setNewClientEmail] = useState('');
  const [newClientPhone, setNewClientPhone] = useState('');
  const [newSiteOpen, setNewSiteOpen] = useState(false);
  const [newSiteName, setNewSiteName] = useState('');
  const [newSiteCity, setNewSiteCity] = useState('');
  const [newSiteProvince, setNewSiteProvince] = useState('');
  // Default Country to Canada — most BGC sites are in Canada, and it immediately
  // populates the Province / State dropdown instead of leaving it empty.
  const [newSiteCountry, setNewSiteCountry] = useState<string>('Canada');
  const newSiteProvinceOptions = useMemo(() => provincesForCountry(newSiteCountry), [newSiteCountry]);

  useEffect(() => {
    setClients(initialClients);
    setSites(initialSites);
  }, [initialClients, initialSites]);

  useEffect(() => {
    // Always refetch the next ID when the dialog opens — stale cached IDs cause unique-constraint collisions.
    if (open) {
      getNextProjectId().then(setProjectId).catch(() => setProjectId(''));
      // Fetch existing projects for fuzzy-search duplicate warning (TASK-001b)
      getProjects({ pageSize: 9999 }).then((r) => setAllProjects(r.data)).catch(() => {});
    }
  }, [open]);

  const resetForm = () => {
    setProjectId(''); setProjectName(''); setSelectedClient(null);
    setSelectedSite(null); setProjectContact(''); setStatus('Active');
    setProjectType('External');
    setClientContactName(''); setClientContactEmail(''); setClientContactPhone('');
    setExpectedStartDate(todayStr()); setExpectedEndDate(''); setComments(''); setErrors({});
  };

  const handleClose = () => { resetForm(); onClose(); };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!projectId.trim()) errs.projectId = 'Project ID is required';
    if (!projectName.trim()) errs.projectName = 'Project Name is required';
    if (!selectedClient) errs.client = 'Client is required';
    if (!selectedSite) errs.site = 'Site is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      // For external projects, persist client contact details onto the Client itself
      // (one client → one primary contact for now; multi-contact deferred to TASK-018).
      if (projectType === 'External' && selectedClient
          && (clientContactName.trim() || clientContactEmail.trim() || clientContactPhone.trim())) {
        try {
          await updateClient(selectedClient.ClientKey, {
            ClientName: selectedClient.ClientName,
            ContactPerson: clientContactName.trim() || undefined,
            Email: clientContactEmail.trim() || undefined,
            Phone: clientContactPhone.trim() || undefined,
          });
        } catch { /* non-fatal — continue with project creation */ }
      }
      const project = await createProject({
        ProjectId: projectId.trim(),
        ProjectName: projectName.trim(),
        ClientKey: selectedClient?.ClientKey ?? '',
        SiteKey: selectedSite?.SiteKey ?? '',
        Status: status,
        ApprovalStatus: 'Pending',
        ProjectLocation: selectedSite?.SiteCity || undefined,
        ExpectedStartDate: expectedStartDate,
        ExpectedEndDate: expectedEndDate || undefined,
        ProjectContact: projectContact.trim(),
        ProjectType: projectType,
        Comments: comments.trim() || undefined,
      });
      resetForm();
      onCreated(project);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      if (msg?.toLowerCase().includes('already exists')) {
        setErrors({ projectId: msg });
      } else {
        setErrors({ submit: msg || 'Failed to create project. Please try again.' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateClient = async () => {
    if (!newClientName.trim()) return;
    try {
      const client = await createClient({
        ClientName: newClientName.trim(),
        ContactPerson: newClientContact.trim() || undefined,
        Email: newClientEmail.trim() || undefined,
        Phone: newClientPhone.trim() || undefined,
      });
      const updated = await getClients();
      setClients(updated);
      setSelectedClient(client);
      // Pre-populate the project-level contact fields from the new client
      if (newClientContact.trim()) setClientContactName(newClientContact.trim());
      if (newClientEmail.trim()) setClientContactEmail(newClientEmail.trim());
      if (newClientPhone.trim()) setClientContactPhone(newClientPhone.trim());
      setNewClientOpen(false);
      setNewClientName(''); setNewClientContact(''); setNewClientEmail(''); setNewClientPhone('');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setErrors((prev) => ({ ...prev, newClient: msg || 'Failed to create client' }));
    }
  };

  const handleCreateSite = async () => {
    if (!newSiteName.trim()) return;
    // City is optional now (TASK-003); require at least one geographic hint.
    if (!newSiteCity.trim() && !newSiteProvince.trim() && !newSiteCountry.trim()) {
      setErrors((prev) => ({ ...prev, newSiteCity: 'Enter at least a City, Province, or Country' }));
      return;
    }
    try {
      const site = await createSite({
        SiteName: newSiteName.trim(),
        SiteCity: newSiteCity.trim() || null,
        Province: newSiteProvince.trim() || null,
        Country: newSiteCountry.trim() || null,
      });
      const updated = await getSites();
      setSites(updated);
      setSelectedSite(site);
      setNewSiteOpen(false);
      setNewSiteName(''); setNewSiteCity(''); setNewSiteProvince(''); setNewSiteCountry('Canada');
    } catch {
      setErrors((prev) => ({ ...prev, newSite: 'Failed to create site' }));
    }
  };

  const clientOptions: (Client | { ClientKey: string; ClientName: string })[] = [
    ...clients, { ClientKey: ADD_NEW_CLIENT, ClientName: '+ Create new client...' },
  ];
  const siteOptions: (Site | { SiteKey: string; SiteName: string; SiteCity: string })[] = [
    ...sites, { SiteKey: ADD_NEW_SITE, SiteName: '+ Create new site...', SiteCity: '' },
  ];

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>New Project</DialogTitle>
        <Divider />
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2.5 }}>
          {/* Row: Project ID + Project Name */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 2 }}>
            <TextField size="small" label="Project ID" required value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              error={!!errors.projectId} helperText={errors.projectId} />
            <TextField size="small" label="Project Name" required value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              error={!!errors.projectName} helperText={errors.projectName} />
          </Box>
          <DuplicateWarning
            value={projectName}
            candidates={allProjects}
            nameSelector={(p) => p.ProjectName}
            secondarySelector={(p) => p.ProjectId}
            label="Similar projects already exist:"
          />

          {/* Row: Site + Client */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <Autocomplete size="small" options={siteOptions}
              getOptionLabel={(opt) => ('SiteName' in opt ? opt.SiteName : '')}
              value={selectedSite}
              onChange={(_e, val) => {
                if (val && 'SiteKey' in val && val.SiteKey === ADD_NEW_SITE) {
                  setNewSiteOpen(true);
                } else { setSelectedSite(val as Site | null); }
              }}
              renderInput={(params) => (
                <TextField {...params} label="Site" required placeholder="Select or create a site..." error={!!errors.site} helperText={errors.site} />
              )} />
            <Autocomplete size="small" options={clientOptions}
              getOptionLabel={(opt) => ('ClientName' in opt ? opt.ClientName : '')}
              value={selectedClient}
              onChange={(_e, val) => {
                if (val && 'ClientKey' in val && val.ClientKey === ADD_NEW_CLIENT) {
                  setNewClientOpen(true);
                } else { setSelectedClient(val as Client | null); }
              }}
              renderInput={(params) => (
                <TextField {...params} label="Client" required placeholder="Select or create a client..." error={!!errors.client} helperText={errors.client} />
              )} />
          </Box>

          {/* Row: City (from site) + Project Type */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField size="small" label="City" value={selectedSite?.SiteCity || ''}
              InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }}
              helperText={selectedSite ? undefined : 'Select a site above'} />
            <TextField size="small" select label="Project Type" value={projectType}
              onChange={(e) => setProjectType(e.target.value as 'Internal' | 'External')}>
              <MenuItem value="External">External (client project)</MenuItem>
              <MenuItem value="Internal">Internal (BGC)</MenuItem>
            </TextField>
          </Box>

          {/* Row: Project Contact (label depends on type) */}
          <TextField size="small"
            label={projectType === 'Internal' ? 'BGC Project Manager' : 'Project Contact'}
            value={projectContact}
            onChange={(e) => setProjectContact(e.target.value)}
            helperText={projectType === 'Internal' ? 'Internal PM name' : 'Primary contact name on the BGC side'} />

          {/* External-only: Client contact details */}
          {projectType === 'External' && (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
              <TextField size="small" label="Client Contact Name"
                value={clientContactName} onChange={(e) => setClientContactName(e.target.value)} />
              <TextField size="small" type="email" label="Client Contact Email"
                value={clientContactEmail} onChange={(e) => setClientContactEmail(e.target.value)} />
              <TextField size="small" label="Client Contact Phone"
                value={clientContactPhone} onChange={(e) => setClientContactPhone(e.target.value)} />
            </Box>
          )}

          {/* Row: Status */}
          <TextField size="small" select label="Status" value={status}
            onChange={(e) => setStatus(e.target.value)}>
            {newProjectStatuses.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>

          {/* Row: Expected Start Date + Expected End Date */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField size="small" type="date" label="Expected Start Date" value={expectedStartDate}
              onChange={(e) => setExpectedStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField size="small" type="date" label="Expected End Date" value={expectedEndDate}
              onChange={(e) => setExpectedEndDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Box>

          <TextField size="small" label="Comments" multiline rows={2} value={comments}
            onChange={(e) => setComments(e.target.value)} />

          {errors.submit && (
            <Typography variant="body2" color="error">{errors.submit}</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}
            sx={{ textTransform: 'none', fontWeight: 600, px: 3, borderRadius: 1.5 }}
            startIcon={submitting ? <CircularProgress size={16} /> : undefined}>
            Create Project
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add New Client sub-dialog */}
      <Dialog open={newClientOpen} onClose={() => setNewClientOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>Add New Client</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <TextField size="small" label="Client Name" value={newClientName}
            onChange={(e) => setNewClientName(e.target.value)} autoFocus />
          <DuplicateWarning
            value={newClientName}
            candidates={clients}
            nameSelector={(c) => c.ClientName}
            label="Existing clients with similar names:"
          />
          <TextField size="small" label="Contact Person" value={newClientContact}
            onChange={(e) => setNewClientContact(e.target.value)} />
          <TextField size="small" type="email" label="Contact Email" value={newClientEmail}
            onChange={(e) => setNewClientEmail(e.target.value)} />
          <TextField size="small" label="Contact Phone" value={newClientPhone}
            onChange={(e) => setNewClientPhone(e.target.value)} />
          {errors.newClient && (
            <Typography variant="body2" color="error">{errors.newClient}</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNewClientOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateClient}
            sx={{ textTransform: 'none', fontWeight: 600 }}>Add</Button>
        </DialogActions>
      </Dialog>

      {/* Add New Site sub-dialog */}
      <Dialog open={newSiteOpen} onClose={() => setNewSiteOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>Add New Site</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <TextField size="small" label="Site Name" required value={newSiteName}
            onChange={(e) => setNewSiteName(e.target.value)} autoFocus />
          <DuplicateWarning
            value={newSiteName}
            candidates={sites}
            nameSelector={(s) => s.SiteName}
            secondarySelector={(s) => s.SiteCity || ''}
            label="Existing sites with similar names:"
          />
          {/* Country + Province first so the country-driven Province list populates
              before the user thinks about the city. Country defaults to Canada. */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <Autocomplete size="small" freeSolo options={COUNTRIES}
              value={newSiteCountry}
              onChange={(_e, v) => {
                setNewSiteCountry(typeof v === 'string' ? v : v || '');
                setNewSiteProvince('');
              }}
              onInputChange={(_e, v) => setNewSiteCountry(v || '')}
              renderInput={(params) => <TextField {...params} label="Country" />} />
            <Autocomplete size="small" freeSolo options={newSiteProvinceOptions}
              value={newSiteProvince}
              onChange={(_e, v) => setNewSiteProvince(typeof v === 'string' ? v : v || '')}
              onInputChange={(_e, v) => setNewSiteProvince(v || '')}
              renderInput={(params) => <TextField {...params} label="Province / State" />} />
          </Box>
          <TextField size="small" label="City" value={newSiteCity}
            onChange={(e) => setNewSiteCity(e.target.value)}
            error={!!errors.newSiteCity}
            helperText={errors.newSiteCity || 'Optional'} />
          {errors.newSite && (
            <Typography variant="body2" color="error">{errors.newSite}</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNewSiteOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateSite}
            sx={{ textTransform: 'none', fontWeight: 600 }}>Add</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
